/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Skeleton,
  Alert,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import NoRecordFound from "src/components/shared/molecules/NoRecordFound";
import { useDispatch, useSelector } from "react-redux";
import { meHandler } from "src/store/reducers/meSlice";
import CREATE_NEW_SUBSCRIPTION from "src/graphql/query/subscription/createNewSubscription";
import { IPrices } from "src/components/pages/PricingPlans/dataTypes";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { useLazyQuery, useMutation } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import GET_CURRENT_SUBSCRIPTION from "src/graphql/query/settings/getCurrentSubscriptions";
import { useNavigate } from "react-router-dom";

import useStyle from "./Styles";

interface ICurrentSubsData {
  maxUsersCount: number;
  currentUsersCount: number;
  currentDeletedCount: number;
  price: number;
  interval: string;
  packageName: string;
  intervalCount: number;
}

interface IPlan {
  name: string;
  productId: string;
  description: string;
  prices: IPrices[];
}

interface Props {
  upgradingPlanData?: IPlan;
  plan: string;
  handleClose: any;
  handleUpgradePlan: any;
  updateSubscriptionLoading: any;
  readSelectedValues: any;
}

const PlanSelection: React.FC<Props> = ({
  upgradingPlanData,
  plan,
  handleClose,
  handleUpgradePlan,
  updateSubscriptionLoading,
  readSelectedValues,
}) => {
  const { classes } = useStyle();

  const { subscriptionId, stripeId } = useActiveUserDetail();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isNewPlan, setIsNewPlan] = useState<boolean>(false);
  const [progress, setProgress] = useState("");
  const [relativeAgentsCount, setRelativeAgentsCount] = useState(0);

  const [agentsNumber, setAgentsNumber] = useState(1);
  const [label, setLabel] = useState("billed monthly");
  const [perPeriodName, setPerPeriodName] = useState("");

  const [calculatedPrice, setCalculatedPrice] = useState(0);

  const [period, setPeriod] = useState(
    JSON.stringify({ value: "month-1", label: "Billed Monthly" }),
  );
  const [selectedInterval, setSelectedInterval] = useState({
    interval: "month",
    intervalCount: 1,
  });

  const { me }: any = useSelector((state: any) => state.me);

  const [currentSubscriptionData, setCurrentSubscriptionData] =
    useState<ICurrentSubsData>({
      maxUsersCount: 0,
      currentUsersCount: 0,
      currentDeletedCount: 0,
      price: 0,
      interval: "monthly",
      packageName: "",
      intervalCount: 1,
    });

  const [subscriptionError, setSubscriptionError] = useState("");

  // passing in the data to the parent
  useEffect(() => {
    readSelectedValues(
      selectedInterval.interval,
      selectedInterval.intervalCount,
      agentsNumber,
      relativeAgentsCount,
      currentSubscriptionData?.currentUsersCount,
      currentSubscriptionData?.currentDeletedCount,
    );
  }, [
    selectedInterval,
    agentsNumber,
    relativeAgentsCount,
    currentSubscriptionData,
  ]);

  const [
    getCurrentSubscription,
    {
      data: getCurrentSubscriptionResult,
      loading: getCurrentSubscriptionLoading,
      error: getCurrentSubscriptionError,
    },
  ] = useLazyQuery(GET_CURRENT_SUBSCRIPTION, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const result =
      getCurrentSubscriptionResult?.getCurrentSubscriptions?.result;
    if (!result) return;

    setCurrentSubscriptionData(result);
    setAgentsNumber(result.maxUsersCount ?? 1);
  }, [getCurrentSubscriptionResult]);

  useEffect(() => {
    if (getCurrentSubscriptionError) {
      setSubscriptionError(getCurrentSubscriptionError.message);
      enqueueSnackbar(getCurrentSubscriptionError.message, {
        variant: "error",
      });
    }
  }, [getCurrentSubscriptionError]);

  useEffect(() => {
    // when user has subscription
    if (me && me.subscriptionId) {
      getCurrentSubscription({
        variables: {
          subscriptionId: me.subscriptionId,
          stripeCustomerId: stripeId,
        },
      });
    } else {
      setCurrentSubscriptionData((prev) => ({
        ...prev,
        maxUsersCount: me ? me.maxCount : 0,
        currentUsersCount: me ? me.currentCount : 0,
        price: 0,
        interval: "monthly",
        packageName: me ? me.plans : "",
      }));
    }
  }, [me]);
  //
  // ---

  useEffect(() => {
    setSelectedInterval({
      ...selectedInterval,
      interval: plan === "monthly" ? "month" : "year",
      intervalCount: 1,
    });
    //
    setPeriod(
      JSON.stringify({
        value: plan === "monthly" ? "month-1" : "year-1",
        label: plan === "monthly" ? "Billed Monthly" : "Billed Annually",
      }),
    );
    setLabel(plan === "monthly" ? "billed monthly" : "Billed Annually");
  }, [plan, upgradingPlanData]);

  const handlePlanChange = (e: any) => {
    const selectedPeriod = JSON.parse(e.target.value);
    setPeriod(e.target.value);
    setLabel(selectedPeriod.label);

    const interval = selectedPeriod.value.split("-")[0];
    const intervalCount = Number(selectedPeriod.value.split("-")[1]);

    setSelectedInterval({
      interval,
      intervalCount,
    });
  };

  const getPricesAccordingToPlan = (pricesList: IPrices[]) => {
    const interval = selectedInterval.interval || "month";
    const intervalCount = selectedInterval.intervalCount || 1;

    if (interval === "month") {
      const montlyPrice = pricesList.find((item) => item.interval === "month");

      if (montlyPrice) {
        return Math.ceil(montlyPrice.price / 100);
      }
    }
    if (interval === "year") {
      const annualPrice = pricesList.find(
        (item) =>
          item.interval === "year" && item.intervalCount === intervalCount,
      );
      if (annualPrice) {
        return Math.ceil(annualPrice.price / 100 / (12 * intervalCount));
      }
    }

    return null;
  };

  const getPriceAccordingToPlanString = useMemo(
    () =>
      getPricesAccordingToPlan(
        upgradingPlanData ? upgradingPlanData.prices : [],
      ),
    [selectedInterval, plan, upgradingPlanData],
  );

  const calculatedPriceFunc = () => {
    const upgradingPlanDataPricesArr = upgradingPlanData
      ? [...upgradingPlanData.prices]
      : [];
    const packagePriceBasedOnIntervalAndCount = upgradingPlanDataPricesArr.find(
      (curr) =>
        curr.interval === selectedInterval.interval &&
        curr.intervalCount === selectedInterval.intervalCount,
    );

    if (packagePriceBasedOnIntervalAndCount) {
      return (packagePriceBasedOnIntervalAndCount.price / 100) * agentsNumber;
    }

    return 0;
  };

  const determineNewPlanFunc = () => {
    const upgradingPlanDataPricesArr = upgradingPlanData
      ? [...upgradingPlanData.prices]
      : [];
    const packagePriceBasedOnIntervalAndCount = upgradingPlanDataPricesArr.find(
      (curr) =>
        curr.interval === selectedInterval.interval &&
        curr.intervalCount === selectedInterval.intervalCount,
    );

    let calculatedPrice_ = 0;
    if (packagePriceBasedOnIntervalAndCount) {
      calculatedPrice_ =
        (packagePriceBasedOnIntervalAndCount.price / 100) * agentsNumber;
    }

    let currentPackagePrice = 0;
    if (currentSubscriptionData) {
      const maxCount = currentSubscriptionData.maxUsersCount || 0;
      const pricePerIntervalPerAgent = currentSubscriptionData.price || 0;
      currentPackagePrice = maxCount * pricePerIntervalPerAgent;
    }

    return currentPackagePrice === calculatedPrice_;
  };

  const upOrDowngradeFunc = () => {
    const upgradingPlanDataPricesArr = upgradingPlanData
      ? [...upgradingPlanData.prices]
      : [];
    const packagePriceBasedOnIntervalAndCount = upgradingPlanDataPricesArr.find(
      (curr) =>
        curr.interval === selectedInterval.interval &&
        curr.intervalCount === selectedInterval.intervalCount,
    );

    let currentSubscriptionData_ = {
      maxUsersCount: 0,
      price: 0,
      interval: "",
      intervalCount: 0,
      packageName: "",
    };

    if (currentSubscriptionData)
      currentSubscriptionData_ = currentSubscriptionData;

    const currentMaxCount = currentSubscriptionData_.maxUsersCount;
    const currentPrice = currentSubscriptionData_.price;
    const currentInterval = currentSubscriptionData_.interval;
    const currentIntervalCount = currentSubscriptionData_.intervalCount;
    const currentTotalPrice = currentPrice * currentMaxCount;
    const currentPlan = currentSubscriptionData_.packageName.toLowerCase();

    const newMaxCount = agentsNumber;
    const newPrice = (packagePriceBasedOnIntervalAndCount?.price || 0) / 100;
    const newInterval = selectedInterval.interval;
    const newIntervalCount = selectedInterval.intervalCount;
    const newTotalPrice = newPrice * newMaxCount;
    const newPlan = upgradingPlanData?.name.toLowerCase();

    let upgradeDetected = false;
    let downgradeDetected = false;

    // --- 1. Agent quantity change ---
    if (newMaxCount > currentMaxCount) upgradeDetected = true;
    if (newMaxCount < currentMaxCount) downgradeDetected = true;

    // --- 2. Interval or intervalCount change ---
    if (
      newInterval !== currentInterval ||
      newIntervalCount !== currentIntervalCount
    ) {
      if (newTotalPrice > currentTotalPrice) upgradeDetected = true;
      if (newTotalPrice < currentTotalPrice) downgradeDetected = true;
    }

    // --- 3. Price change ---
    if (newPrice > currentPrice) upgradeDetected = true;
    if (newPrice < currentPrice) downgradeDetected = true;

    // --- 4. Plan tier change ---
    if (currentPlan === "basic" && newPlan !== "basic") upgradeDetected = true;
    if (currentPlan !== "basic" && newPlan === "basic")
      downgradeDetected = true;

    // --- Final decision ---
    if (upgradeDetected && !downgradeDetected) return "upgrade";
    if (downgradeDetected && !upgradeDetected) return "downgrade";
    if (upgradeDetected && downgradeDetected) {
      // If both happen, choose priority rule — here, upgrades win
      return "upgrade";
    }
    return "same";
  };

  const agentsCountReducedOrIncreased = () => {
    let currentCount = 0;
    if (currentSubscriptionData)
      currentCount = currentSubscriptionData.currentUsersCount;

    return agentsNumber - currentCount;
  };

  const perPeriodNameFunc = () => {
    if (selectedInterval.interval === "month") return "mo";

    switch (selectedInterval.intervalCount) {
      case 1:
        return "yr";
      case 2:
        return "2yr";
      case 3:
        return "3yr";
      default:
        return "";
    }
  };

  useEffect(() => {
    setCalculatedPrice(calculatedPriceFunc());
    setPerPeriodName(perPeriodNameFunc());
    setIsNewPlan(determineNewPlanFunc());
    setProgress(upOrDowngradeFunc());
    setRelativeAgentsCount(agentsCountReducedOrIncreased());
  }, [selectedInterval, agentsNumber, upgradingPlanData]);

  const selectingPlans = [
    { value: "month-1", label: "Billed Monthly" },
    { value: "year-1", label: "Billed Annually" },
    { value: "year-2", label: "Billed Every 2 Years" },
    { value: "year-3", label: "Billed Every 3 Years" },
  ];

  const [
    createNewSubscription,
    {
      loading: createNewSubscriptionLoading,
      error: createNewSubscriptionError,
      data: createNewSubscriptionResult,
    },
  ] = useMutation(CREATE_NEW_SUBSCRIPTION, { fetchPolicy: "network-only" });

  useEffect(() => {
    const res = createNewSubscriptionResult?.createNewSubscription;
    if (!res) return;

    const { success, error, result } = res;

    if (success) {
      handleClose();
      // updating ME data
      let data;
      try {
        data = JSON.parse(result);
      } catch (e) {
        enqueueSnackbar("Invalid server response", { variant: "error" });
        return;
      }

      const {
        id: createdSubscriptionId,
        maxAgents,
        new_plan,
        new_plan_status,
        status,
      } = data;
      dispatch(
        meHandler({
          maxCount: maxAgents,
          subscriptionId: createdSubscriptionId,
          new_plan,
          new_plan_status,
          stripe_subscription_status: status,
        }),
      );
      enqueueSnackbar(
        `Subscription has been created for ${upgradingPlanData?.name}`,
        {
          variant: "success",
        },
      );
      //
      setTimeout(() => {
        navigate("/settings/billing");
      }, 500);
    } else if (error) {
      enqueueSnackbar(createNewSubscriptionResult.createNewSubscription.error, {
        variant: "error",
      });
    }
  }, [createNewSubscriptionResult]);

  useEffect(() => {
    if (createNewSubscriptionError) {
      enqueueSnackbar(createNewSubscriptionError.message, {
        variant: "error",
      });
    }
  }, [createNewSubscriptionError]);

  const handleCreatePlan = async () => {
    const upgradingPlanDataPricesArr = upgradingPlanData
      ? [...upgradingPlanData.prices]
      : [];

    const packagePriceBasedOnIntervalAndCount = upgradingPlanDataPricesArr.find(
      (curr) =>
        curr.interval === selectedInterval.interval &&
        curr.intervalCount === selectedInterval.intervalCount,
    );

    await createNewSubscription({
      variables: {
        customerId: stripeId,
        priceId: packagePriceBasedOnIntervalAndCount?.priceId,
        noOfAgents: agentsNumber,
        planName: upgradingPlanData?.name,
      },
    });
  };

  if (!upgradingPlanData)
    return (
      <Box className={classes.notFoundBox}>
        <NoRecordFound text="no plan is selected or loaded" />
      </Box>
    );

  if (subscriptionError)
    return (
      <Alert severity="error" icon={<ErrorOutlineIcon />}>
        <Typography className={classes.currentInfoTextBox}>
          No active subscription found
        </Typography>
      </Alert>
    );

  return (
    <Box>
      <Box className={classes.main}>
        <Typography className={classes.mainHeading}>
          {upgradingPlanData.name}
        </Typography>
        <Typography
          className={classes.mainDescription}
          color="text.secondary"
          variant="body2"
        >
          {upgradingPlanData.description}
        </Typography>
        {/* show price per mon per agent */}
        <Typography className={classes.mainPrice}>
          ${getPriceAccordingToPlanString} / month
        </Typography>

        {/* current plan info */}
        {getCurrentSubscriptionLoading ? (
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "80%", margin: "auto" }}
          />
        ) : (
          <Box className={classes.currentPlanInfoBox}>
            <ErrorOutlineIcon className={classes.infoErrorIcon} />
            <Typography className={classes.currentInfoTextBox}>
              Current plan:{" "}
              <span className={classes.countFigure}>
                {currentSubscriptionData.currentUsersCount}
              </span>{" "}
              out of{" "}
              <span className={classes.countFigure}>
                {currentSubscriptionData.maxUsersCount}
              </span>{" "}
              users for{" "}
              {currentSubscriptionData.packageName.toLowerCase() ===
                "freetrial" &&
                currentSubscriptionData.packageName.toLowerCase()}
            </Typography>
            {me && me.subscriptionId && (
              <Typography className={classes.currentInfoPriceBox}>
                ${currentSubscriptionData.price}
                <span className={classes.currentInfoPriceText}>
                  /{currentSubscriptionData.interval}
                </span>
                <span className={classes.currentInfoPriceText}>/agent</span>
              </Typography>
            )}
          </Box>
        )}
        {/* form part */}
        <Box className={classes.formBox}>
          <FormControl fullWidth sx={{ flex: 1 }}>
            <Select id="interval" value={period} onChange={handlePlanChange}>
              {selectingPlans.map((planItem) => (
                <MenuItem key={planItem.value} value={JSON.stringify(planItem)}>
                  {planItem.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} display="flex" alignItems="center" mb="20px">
            <TextField
              type="number"
              variant="outlined"
              defaultValue={1}
              inputProps={{ min: 1 }}
              value={agentsNumber}
              onChange={(e) => {
                const value = Math.max(1, Number(e.target.value));
                setAgentsNumber(value);
              }}
            />
            <Typography
              sx={{
                marginLeft: "8px",
              }}
            >
              {`agent${agentsNumber > 1 ? "s" : ""}`}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Footer */}
      <Box className={classes.footerBox}>
        <Box className={classes.footerInfoBox}>
          <Typography className={classes.infoDollar}>$</Typography>
          <Typography className={classes.infoPrice}>
            {calculatedPrice}
          </Typography>
          <Typography className={classes.intervalText}>
            /{perPeriodName} ({label})
          </Typography>
        </Box>

        <Button
          variant="contained"
          className={classes.footerBtn}
          onClick={!subscriptionId ? handleCreatePlan : handleUpgradePlan}
          disabled={
            getCurrentSubscriptionLoading ||
            updateSubscriptionLoading ||
            createNewSubscriptionLoading ||
            isNewPlan
          }
        >
          {getCurrentSubscriptionLoading ? (
            <CircularProgress className={classes.progressIndicator} size={15} />
          ) : (
            <>
              {progress && `${progress} to `}
              {upgradingPlanData.name}{" "}
              {(updateSubscriptionLoading || createNewSubscriptionLoading) && (
                <CircularProgress
                  className={classes.progressIndicator}
                  size={15}
                />
              )}
            </>
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default PlanSelection;
